'use client';

import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import {
  Cog6ToothIcon, PlusCircleIcon,
} from '@heroicons/react/24/outline';
import { Anchor, TailwindSpinner, MissionControlSwitch, bjToast } from '@components/common';
import { useSwrNextJobs } from '@hooks/useSwrApi/endpoints/next-jobs';
import { useSidebarNavigation, CandidateSearchFilters } from '@components/contexts/SidebarNavigationContext';
import { buildKeywordWithSalary } from '@utils/strings/keywordSalary';
import { slugify } from '@utils/strings/slugify';
import { getI18nLinkHref } from '@services/i18nPaths';
import { fetcher, isApiV1Errors } from '@utils/data-fetching';
import type { JobSearchQueryParams } from '@type/filters';
import { MemoizedCompactJobAlertCard } from './CompactJobAlertCard';
import { POPULAR_SEARCH_KEYS } from './popularSearchKeys';
import { GuestJobsSidebar } from './GuestJobsSidebar';

const ITEMS_MAX = 10; // Show up to 10 items from NextJobData

interface CandidateJobsSidebarProps {
  locale: Locale;
  className?: string;
  isAuthenticated?: boolean;
  showAccountSettings?: boolean;
  onSwitchContext?: () => void;
  /** Opens the Jobs on Map modal. Provided by MissionControl to avoid import cycles. */
  onOpenMap?: (queryParams?: Partial<JobSearchQueryParams>) => void;
}

/**
 * @description Inner component that displays job alerts for authenticated candidates.
 */
const CandidateJobsSidebarContent = (props: Omit<CandidateJobsSidebarProps, 'isAuthenticated'>) => {
  const {
    locale,
    className,
    showAccountSettings = true,
    onSwitchContext,
    onOpenMap,
  } = props;
  const { t } = useTranslation('common');

  // Get navigation context
  const {
    currentView, navigateToNewJobSearchForm, navigateToCandidateSearch, navigateToKeywordSearch, navigateToEditSearch,
  } = useSidebarNavigation();

  const dispatchMobileClose = () => {
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      window.dispatchEvent(new Event('close-sidebar'));
      window.dispatchEvent(new Event('close-detail-modal'));
    }
  };

  const handleNavigateToNewJobSearchForm = () => { dispatchMobileClose(); navigateToNewJobSearchForm(); };
  const handleNavigateToAlert = (filters: CandidateSearchFilters) => { dispatchMobileClose(); navigateToCandidateSearch(filters); };
  const handleNavigateToKeywordSearch = (keyword: string) => { dispatchMobileClose(); navigateToKeywordSearch(keyword); };

  // Fetch unified next jobs data (combines job alerts and applications)
  const { data: nextJobsData, isLoading, mutate: mutateNextJobs } = useSwrNextJobs({ locale });

  // Build combined list of all cards from NextJobData
  const allCards = useMemo(() => {
    if (!nextJobsData || nextJobsData.length === 0) return [];

    return nextJobsData.slice(0, ITEMS_MAX).map((item) => ({
      id: `next-job-${item.id}`,
      nextJobId: typeof item.id === 'number' ? item.id : undefined,
      isAlert: item.sourceType === 'job_search_alert',
      title: item.jobTitle,
      keyword: item.jobTitle,
      location: item.locations?.[0],
      locations: item.locations,
      salary: item.salary ?? undefined,
      originalKeyword: item.originalKeyword ?? undefined,
      decomposedFilters: item.decomposedFilters,
    }));
  }, [nextJobsData]);

  const handleDeleteNextJob = (id: number) => {
    void fetcher<object>(`/v1/user/next-jobs/${id}`, locale, { method: 'DELETE' }).then((response) => {
      if (isApiV1Errors(response)) {
        bjToast.error(response.errors[0].message);
        return;
      }
      void mutateNextJobs();
    });
  };

  // Derive the active candidateSearch keyword/locations from context for selection highlighting
  const activeCandidateSearch = useMemo(() => (
    typeof currentView === 'object' && 'candidateSearch' in currentView ? currentView.candidateSearch : undefined
  ), [currentView]);

  // Determine selected keyword search (for keyword cards like last experience / next job)
  const selectedKeyword = useMemo(() => (typeof currentView === 'object' && 'keyword' in currentView ? currentView.keyword : undefined), [currentView]);

  return (
    <div className={`flex h-full flex-col bg-surface ${className}`}>
      {/* Top content - scrollable */}
      <div className="scrollbar-hide flex-1 overflow-y-auto px-4 pb-4 pt-0">
        {/* Navigation Header - Links on top */}
        <div className="mb-6 flex flex-col gap-3 border-b border-input pb-4">
          {/* Switch Context - Segmented control: Hiring | Job search */}
          <MissionControlSwitch
            activeContext="candidate"
            hireLabel={t('mc.switch.hire')}
            jobSearchLabel={t('mc.switch.job.search')}
            onSwitch={onSwitchContext ?? (() => {})}
          />

          {/* Role-searched - Show new job search form in main content */}
          <button
            type="button"
            onClick={handleNavigateToNewJobSearchForm}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium text-ink transition-colors hover:bg-surface-200"
            data-test-id="sidebar-role-searched"
          >
            <PlusCircleIcon className="size-5 text-ink" />
            <span>{t('mc.search.job') || 'Search job'}</span>
          </button>
        </div>

        {/* Job Search Alerts Section */}
        <div className="mb-3">
          {!isLoading && (
            <h3 className="mb-3 px-3 text-sm font-bold text-ink">
              {allCards.length === 0 ? t('user.popular.searches') : t('my-jobs.page.title')}
            </h3>
          )}

          {/* Combined list: Saved Alerts + Applications from NextJobData */}
          <div className="flex flex-col gap-3">
            {/* Loading state */}
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <TailwindSpinner className="size-6 text-primary" />
              </div>
            )}

            {/* Empty state — show popular searches when nothing is available */}
            {allCards.length === 0 && !isLoading && (
              <>
                {POPULAR_SEARCH_KEYS.slice(0, 6).map((key) => {
                  const keyword = t(key);
                  const isSelected = keyword === selectedKeyword;
                  return (
                    <MemoizedCompactJobAlertCard
                      key={key}
                      title={keyword}
                      locale={locale}
                      isSelected={isSelected}
                      onSelect={() => handleNavigateToKeywordSearch(keyword)}
                      onOpenMap={onOpenMap}
                    />
                  );
                })}
              </>
            )}

            {/* All cards from NextJobData */}
            {allCards.map((card) => {
              const isSelected = card.isAlert
                ? activeCandidateSearch?.keyword === card.keyword
                  && JSON.stringify(activeCandidateSearch?.locations) === JSON.stringify(card.locations)
                : card.keyword === selectedKeyword;

              const handleSelect = () => {
                if (card.isAlert && card.keyword) {
                  const filters: CandidateSearchFilters = {
                    keyword: card.keyword,
                    ...(card.locations && { locations: card.locations }),
                  };
                  handleNavigateToAlert(filters);
                } else if (card.keyword) {
                  handleNavigateToKeywordSearch(card.keyword);
                }
              };

              const handleEdit = () => {
                if (card.keyword) {
                  navigateToEditSearch({
                    keyword: card.keyword,
                    ...(card.locations && { locations: card.locations }),
                    ...(card.salary && { salary: card.salary }),
                    ...(card.originalKeyword && { originalKeyword: card.originalKeyword }),
                    ...(card.decomposedFilters && { decomposedFilters: card.decomposedFilters }),
                  });
                }
              };

              const effectiveKeyword = buildKeywordWithSalary(card.keyword, card.salary);
              let editHref: string;
              if (card.locations?.length) {
                const locationSlug = card.locations.map(slugify).join(',');
                const keywordSlug = effectiveKeyword ? `/${slugify(effectiveKeyword)}` : '';
                editHref = `${getI18nLinkHref(`/mc/job/list/location-${locationSlug}`, locale).replace(/%2C/g, ',')}${keywordSlug}?edit=1`;
              } else if (effectiveKeyword) {
                editHref = `${getI18nLinkHref(`/mc/job/list/${slugify(effectiveKeyword)}`, locale)}?edit=1`;
              } else {
                editHref = `${getI18nLinkHref('/mc/job/list', locale)}?edit=1`;
              }

              return (
                <MemoizedCompactJobAlertCard
                  key={card.id}
                  title={card.title}
                  locale={locale}
                  isSelected={isSelected}
                  location={card.location}
                  onSelect={handleSelect}
                  onEdit={handleEdit}
                  editHref={editHref}
                  onOpenMap={onOpenMap}
                  onDelete={card.nextJobId !== undefined ? () => handleDeleteNextJob(card.nextJobId!) : undefined}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Account Settings Link - Bottom aligned, outside scroll area */}
      {showAccountSettings && (
        <div className="shrink-0 border-t border-input bg-surface px-4 py-3">
          <Anchor
            href="/account/settings"
            styling="none"
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-surface-200"
          >
            <Cog6ToothIcon className="size-5 text-ink" />
            <span>{t('account.settings.title') || 'Account Settings'}</span>
          </Anchor>
        </div>
      )}
    </div>
  );
};

/**
 * @description Modular job alerts list sidebar component for candidates.
 * Displays saved job search alerts with "New Job Search" card at top.
 * Shows a sign-in prompt when user is not authenticated.
 */
export const CandidateJobsSidebar = (props: CandidateJobsSidebarProps) => {
  const { isAuthenticated = false, ...restProps } = props;

  // Show authenticated content if user is logged in
  if (isAuthenticated) {
    return <CandidateJobsSidebarContent {...restProps} />;
  }

  return (
    <GuestJobsSidebar
      locale={restProps.locale}
      className={restProps.className}
      onSwitchContext={restProps.onSwitchContext}
      onOpenMap={restProps.onOpenMap}
    />
  );
};
